import { api } from './client'

// MOCK-ONLY API — which follow-up sequence converts (white-labeled "Marketing
// Supercharge"). The real BE has no campaign engine yet; this lives only in
// backend-mock (routes/marketing.ts). Wire types stay here until the real BE
// grows a counterpart.

export interface CampaignResult {
  campaignId: string
  name: string
  channel: 'sms' | 'email'
  enrolled: number
  delivered: number
  replied: number
  scheduled: number
  converted: number
  /** Σ estimate value of the booked + completed rows — the money the sequence moved. */
  bookedValue: number
}

// One row per running conversation — the team inbox.
export interface InboxConversation {
  customerId: number
  name: string
  phone: string | null
  email: string | null
  vehicleLabel: string | null
  estimateId: number
  status: 'active' | 'converted' | 'unresponsive' | 'stopped' | 'not_started'
  needsReply: boolean
  lastMessage: {
    body: string
    direction: 'inbound' | 'outbound'
    occurredAt: string
    channel: 'sms' | 'email'
  } | null
  lastActivityAt: string
  /** The appointment time when this conversation's estimate is booked. */
  scheduledAt: string | null
  /** Raw consent — distinguishes a real opt-out (Do Not Contact) from a
   *  softer pause, and powers the compliance DNC list. */
  consentState: 'subscribed' | 'paused' | 'handled_offline' | 'opted_out'
  consentUpdatedAt: string | null
  consentReason: string | null
  /** The engine's next queued send for this conversation (quiet-clamped). */
  nextTouchAt: string | null
  nextTouchChannel: 'sms' | 'email' | null
  /** Where the sequence stands: it never stops on its own — a reply HOLDS it
   *  until a person answers, a callback PAUSES it until that day. */
  dripState: 'active' | 'paused' | 'held' | 'finished' | 'stopped' | 'converted' | 'not_started'
  dripResumesAt: string | null
  heldBy: 'reply' | 'parked' | null
}

export interface PromotionResult {
  sent: number
  skippedConsent: number
  skippedNoContact: number
  /** Sends attempted outside quiet hours queue for the next morning. */
  heldForMorning: number
}

// The follow-up engine's full report card — derived from the same rules that
// build the threads, so it can never disagree with a customer's card.
export interface MarketingAnalytics {
  messages: { texts: number; emails: number; reminders: number; total: number }
  engagement: {
    emailsOpened: number
    openRate: number
    replies: number
    textReplies: number
    emailReplies: number
    replyRate: number
    medianFirstReplyHours: number | null
  }
  outcomes: {
    enrolled: number
    booked: number
    bookedValue: number
    completed: number
    completedValue: number
    bookingRate: number
  }
  stepPerformance: {
    step: number
    label: string
    channel: 'sms' | 'email'
    sent: number
    repliesAfter: number
  }[]
  /** Daily activity, last 14 days chronological (zero-filled). */
  timeline: { date: string; texts: number; emails: number; replies: number }[]
  now: {
    activeDrips: number
    waitingOnYou: number
    stopped: number
    noContact: number
  }
}

export const campaignsApi = {
  results: (teamId: number | string) =>
    api.get<CampaignResult[]>(`/marketing/team/${teamId}/campaign-results`),
  inbox: (teamId: number | string) =>
    api.get<InboxConversation[]>(`/marketing/team/${teamId}/inbox`),
  analytics: (teamId: number | string) =>
    api.get<MarketingAnalytics>(`/marketing/team/${teamId}/analytics`),
  // Bulk outreach to explicit customers; the server enforces the consent +
  // no-contact guardrails no matter what ids are sent.
  sendPromotion: (customerIds: number[], body: string) =>
    api.post<PromotionResult>('/marketing/promotions', { customerIds, body }),
}
